// src/pages/MainPanel/CreateExperiment/CreateWorkspace.js
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PropTypes from "prop-types";

import {
  INK_SECONDARY,
  INK_MUTED,
  HAIRLINE,
  SURFACE,
  RADIUS,
  SHADOW_SM,
  FONT,
} from "../../../theme/tokens";

import MainPanelContent, {
  CONTENT_MAX_WIDTH,
} from "../components/MainPanelContent";

import GPUResourceForm from "./GPUResourceForm";
import CPUResourceForm from "./CPUResourceForm";
import InputWithSlider from "./components/ResourceInputSlider";
import ResourceGroupSelector from "./components/ResourceGroupSelector";
import DatasetSelector from "./components/DatasetSelector";
import ImageSelector from "./components/ImageSelector";
import {
  TabPanel,
  WizardStepper,
  StepHeader,
  SubHeader,
} from "./components/WizardScaffold";

import { buildWorkspacePreviewYaml } from "./components/previewTemplates";
import ConfigPreviewPanel, {
  ConfigUploadFooter,
} from "./components/ExprConfigPreviewPanel";
import { importWorkspaceConfig } from "./components/ExprConfigImport";
import {
  formatExperimentConfigValidationErrors,
  formatExperimentSubmissionError,
  submitExperimentConfigYaml,
  validateExperimentConfigYaml,
} from "./components/ExprConfigValidation";
import {
  buildConfigValidationState,
  buildPartialImportMessage,
  buildPreviewValidationProps,
  createConfigValidationState,
  createDefaultCpuConfig,
  createDefaultGpuConfig,
  isConfigValidationCurrent,
  toMessageList,
  validationStatusMeta,
} from "./components/ExprConfigStates";

import {
  deriveGpuCpuCores,
  getGpuPartitionSpecs,
  getTargetAllocation,
  validateDnsLabel,
  validateCpuConfig,
  validateGpuConfig,
} from "../../../utils/experimentInputValidation";

import { useProfile } from "../../../context/ProfileContext";
import { useDatasets } from "../../../hooks/useDatasets";
import { useResourceAccessLimits } from "../../../hooks/useResourceAccessLimits";

/* -------------------- Wizard scaffolding -------------------- */
const STEPS = [
  { label: "Workflow Image", caption: "Optional base image" },
  { label: "Name & Group", caption: "Identify & allocate" },
  { label: "Resources", caption: "Compute & runtime" },
  { label: "Datasets", caption: "Optional mounts" },
];

const createWorkspaceDraft = () => ({
  workspaceName: "",
  selectedGroup: null,
  gpuConfig: createDefaultGpuConfig(),
  cpuConfig: createDefaultCpuConfig(),
  selectedDatasets: [],
  imageRef: "",
});

const CreateWorkspace = ({ user, onCancel, onSuccess, onGoToDatasets }) => {
  const { profile, fetchProfile } = useProfile();
  const { datasets: availableDatasets, error: datasetsError } = useDatasets();

  // Form state
  const [draft, setDraft] = useState(createWorkspaceDraft);
  const {
    workspaceName,
    selectedGroup,
    gpuConfig,
    cpuConfig,
    selectedDatasets,
    imageRef,
  } = draft;
  const [tab, setTab] = useState(0);
  const [imageRefError, setImageRefError] = useState("");
  const importInputRef = useRef(null);

  // Server interaction state
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [importWarnings, setImportWarnings] = useState([]);
  const [importError, setImportError] = useState("");
  const [importSuccess, setImportSuccess] = useState("");
  const [configValidation, setConfigValidation] = useState(
    createConfigValidationState,
  );

  // Resource identity and availability
  const resourceAvailability = useResourceAccessLimits({
    selectedGroup,
    gpuProduct: gpuConfig.gpuProduct,
    cpuConfig,
    gpuConfig,
    isSubmitJob: false,
  });

  // Derived: workspace type, target allocation, and partition specs
  const workspaceType = selectedGroup
    ? selectedGroup.name === "CPU"
      ? "cpu"
      : "gpu"
    : null;
  const targetAllocation = useMemo(
    () =>
      getTargetAllocation(
        selectedGroup,
        workspaceType === "gpu" ? gpuConfig.gpuProduct : null,
      ),
    [selectedGroup, workspaceType, gpuConfig.gpuProduct],
  );

  // The walltime limit is fixed by the selected partition.
  const workspaceWalltimeHours =
    targetAllocation?.max_walltime_interactive ?? 1;
  const gpuPartitionSpecs = useMemo(
    () => getGpuPartitionSpecs(selectedGroup, gpuConfig.gpuProduct),
    [selectedGroup, gpuConfig.gpuProduct],
  );

  // Draft mutation helpers
  const patchDraft = useCallback((patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  const patchGpuConfig = useCallback((patch) => {
    setDraft((prev) => ({
      ...prev,
      gpuConfig: { ...prev.gpuConfig, ...patch },
    }));
  }, []);

  const patchCpuConfig = useCallback((patch) => {
    setDraft((prev) => ({
      ...prev,
      cpuConfig: { ...prev.cpuConfig, ...patch },
    }));
  }, []);

  const applySelectedGroup = useCallback((group, overrides = {}) => {
    const isGpuGroup = group ? group.name !== "CPU" : false;
    setDraft((prev) => ({
      ...prev,
      selectedGroup: group,
      gpuConfig: {
        ...createDefaultGpuConfig(),
        ...(isGpuGroup ? (overrides.gpuConfig ?? {}) : {}),
      },
      cpuConfig: {
        ...createDefaultCpuConfig(),
        ...(!isGpuGroup ? (overrides.cpuConfig ?? {}) : {}),
      },
    }));
  }, []);

  // Profile and default resource-group initialization
  useEffect(() => {
    if (!profile && user?.id) fetchProfile(user.id);
  }, [profile, user, fetchProfile]);

  useEffect(() => {
    if (selectedGroup || !profile?.groups?.length) return;
    const cpuGroup = profile.groups.find((group) => group.name === "CPU");
    applySelectedGroup(cpuGroup || profile.groups[0]);
  }, [applySelectedGroup, profile?.groups, selectedGroup]);

  const handleNameChange = (e) => {
    patchDraft({ workspaceName: e.target.value });
  };

  // Derived form values and local validation
  const workspaceLabelValidation = useMemo(
    () => validateDnsLabel(workspaceName),
    [workspaceName],
  );
  const { errors: labelErrors, isValid: isLabelDNSValid } =
    workspaceLabelValidation;

  const derivedGpuConfig = useMemo(() => {
    return {
      ...gpuConfig,
      cpuCores: deriveGpuCpuCores(gpuConfig.gpuCount, gpuPartitionSpecs),
    };
  }, [gpuConfig, gpuPartitionSpecs]);

  const cpuValidation = useMemo(
    () =>
      validateCpuConfig(cpuConfig, {
        selectedGroup,
        effectiveLimits: resourceAvailability.cpuEffectiveLimits,
      }),
    [cpuConfig, selectedGroup, resourceAvailability.cpuEffectiveLimits],
  );
  const gpuValidation = useMemo(
    () =>
      validateGpuConfig(derivedGpuConfig, {
        selectedGroup,
        selectedGPUProduct: resourceAvailability.accessibleGPUs.find(
          (gpu) => gpu.type === derivedGpuConfig.gpuProduct,
        ),
        accessibleGPUs: resourceAvailability.accessibleGPUs,
        effectiveLimits: resourceAvailability.gpuEffectiveLimits,
      }),
    [
      derivedGpuConfig,
      selectedGroup,
      resourceAvailability.accessibleGPUs,
      resourceAvailability.gpuEffectiveLimits,
    ],
  );

  const previewYaml = useMemo(() => {
    return buildWorkspacePreviewYaml({
      workspaceType,
      workspaceLabel: workspaceName,
      groupName: selectedGroup?.name ?? null,
      imageRef,
      gpuConfig: derivedGpuConfig,
      cpuConfig,
      selectedDatasets,
    });
  }, [
    workspaceType,
    workspaceName,
    selectedGroup,
    derivedGpuConfig,
    cpuConfig,
    selectedDatasets,
    imageRef,
  ]);

  const configValidated = isConfigValidationCurrent(
    configValidation,
    previewYaml,
  );
  const displayedYaml = configValidated
    ? configValidation.canonicalYaml
    : previewYaml;

  // Keep the validation result tied to the exact YAML that was checked.
  useEffect(() => {
    if (
      configValidation.validatedYaml &&
      configValidation.validatedYaml !== previewYaml
    ) {
      setConfigValidation(createConfigValidationState());
      setImportWarnings([]);
      setImportError("");
      setImportSuccess("");
      setCreateError(null);
    }
  }, [previewYaml, configValidation.validatedYaml]);

  // Expired receipts must return the UI to validation before execution.
  useEffect(() => {
    if (configValidation.status !== "valid") return undefined;
    const expiresAt = Date.parse(configValidation.validationExpiresAt || "");
    const delay = expiresAt - Date.now();
    if (!Number.isFinite(delay) || delay <= 0) {
      setConfigValidation(createConfigValidationState());
      return undefined;
    }
    const timer = setTimeout(
      () => setConfigValidation(createConfigValidationState()),
      delay,
    );
    return () => clearTimeout(timer);
  }, [configValidation.status, configValidation.validationExpiresAt]);

  // Import and backend YAML validation
  const handleImportConfig = async (event) => {
    const [file] = event.target.files || [];
    event.target.value = "";
    if (!file) return;

    setImportWarnings([]);
    setImportError("");
    setImportSuccess("");
    setCreateError(null);
    setConfigValidation(createConfigValidationState());

    try {
      const yamlText = await file.text();
      const validation = await validateExperimentConfigYaml(
        yamlText,
        "workspace",
      );

      const imported = importWorkspaceConfig({
        yamlText,
        groups: profile?.groups || [],
        availableDatasets,
      });
      patchDraft({
        imageRef: imported.values.imageRef,
        workspaceName: imported.values.workspaceName,
        selectedDatasets: imported.values.selectedDatasets,
        gpuConfig: imported.values.gpuConfig,
        cpuConfig: imported.values.cpuConfig,
      });
      if (imported.values.selectedGroup) {
        applySelectedGroup(imported.values.selectedGroup, {
          gpuConfig: imported.values.gpuConfig ?? undefined,
          cpuConfig: imported.values.cpuConfig ?? undefined,
        });
      }

      setImportWarnings(validation.warnings || []);
      setImportError(
        validation.valid
          ? ""
          : formatExperimentConfigValidationErrors(validation),
      );
      setImportSuccess(
        validation.valid
          ? `Imported config from ${file.name}.`
          : buildPartialImportMessage(file.name),
      );
      setCreateError(null);
      setTab(0);
    } catch (error) {
      setImportWarnings([]);
      setImportSuccess("");
      setImportError(error.message || "Failed to import config.");
    }
  };

  const handleValidateConfig = async () => {
    setConfigValidation((prev) => ({
      ...prev,
      status: "validating",
      validatedYaml: previewYaml,
    }));
    setImportWarnings([]);
    setImportError("");
    setImportSuccess("");
    setCreateError(null);

    try {
      const validation = await validateExperimentConfigYaml(
        previewYaml,
        "workspace",
      );
      setImportWarnings(validation.warnings || []);
      const nextValidation = buildConfigValidationState(
        validation,
        previewYaml,
      );
      setConfigValidation(nextValidation);
      if (nextValidation.status !== "valid") {
        setCreateError(
          validation.valid
            ? "Validation succeeded without a submission receipt. Please validate again."
            : formatExperimentConfigValidationErrors(validation),
        );
      }
    } catch (error) {
      setConfigValidation({
        ...createConfigValidationState(),
        status: "invalid",
        validatedYaml: previewYaml,
      });
      setCreateError(
        formatExperimentSubmissionError(error, "Failed to validate config."),
      );
    }
  };

  // Stepper state and navigation
  const isImageRefValid = !imageRefError;
  const resourceHasVisibleError =
    workspaceType === "gpu"
      ? Object.values(gpuValidation.errors).some(Boolean)
      : workspaceType === "cpu"
        ? Object.values(cpuValidation.errors).some(Boolean)
        : false;

  const canValidateConfig =
    !isCreatingWorkspace && configValidation.status !== "validating";

  const hasStepError = (i) => {
    if (i === 0) return !!imageRef.trim() && !isImageRefValid;
    if (i === 1) return !selectedGroup || !isLabelDNSValid;
    if (i === 2) return resourceHasVisibleError;
    if (i === 3) return false;
    return false;
  };

  const isStepEnabled = (i) => {
    return i >= 0 && i < STEPS.length;
  };

  const handleTabChange = (_e, nv) => {
    setTab(nv);
  };

  const handleNext = () => {
    setTab((current) => Math.min(STEPS.length - 1, current + 1));
  };
  const handleBack = () => setTab((t) => Math.max(0, t - 1));

  // Execute the exact canonical YAML and receipt returned by the dry run.
  const handleSubmit = async () => {
    if (!configValidated) {
      return setCreateError(
        "Validate the current config before creating a workspace.",
      );
    }

    setIsCreatingWorkspace(true);
    setCreateError(null);

    try {
      await submitExperimentConfigYaml({
        yamlText: configValidation.canonicalYaml,
        intent: "workspace",
        validationId: configValidation.validationId,
      });
      setShowSuccessDialog(true);
    } catch (error) {
      console.error(error);
      if (error?.data?.valid === false) {
        setImportWarnings(error.data.warnings || []);
      }
      if (error?.status === 409 || error?.status === 422) {
        setConfigValidation(createConfigValidationState());
      }
      setCreateError(
        formatExperimentSubmissionError(error, "Failed to create workspace."),
      );
    } finally {
      setIsCreatingWorkspace(false);
    }
  };

  const canCreate = configValidated && !isCreatingWorkspace;
  const validationMeta =
    validationStatusMeta[configValidation.status] || validationStatusMeta.idle;

  // Render helpers
  const runtimeField = (
    <Box>
      <Alert severity="info" sx={{ mb: 1, py: 0.25 }}>
        <Box component="strong" sx={{ fontWeight: 700 }}>
          Your workspace will be evicted when it hits the runtime limit.
        </Box>{" "}
        Save your work frequently. For longer runtimes, submit a job or contact
        EFabric support to reserve resources.
      </Alert>
      <InputWithSlider
        id="workspace-runtime-hours"
        label="Runtime (hours)"
        description={
          targetAllocation
            ? "Fixed by the selected partition."
            : "Select a partition to view the runtime."
        }
        value={workspaceWalltimeHours}
        onChange={() => {}}
        helperText=""
        disabled
        minInput={1}
        maxInput={Math.max(1, workspaceWalltimeHours)}
      />
    </Box>
  );

  return (
    <MainPanelContent title="Create a Workspace" maxWidth={CONTENT_MAX_WIDTH}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: 3,
          // Stretch children to full width when stacked; top-align in the wide
          // row layout so the two columns keep their natural heights.
          alignItems: { xs: "stretch", lg: "flex-start" },
          width: "100%",
        }}
      >
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            bgcolor: SURFACE,
            border: `1px solid ${HAIRLINE}`,
            borderRadius: `${RADIUS.md}px`,
            boxShadow: SHADOW_SM,
            overflow: "hidden",
          }}
        >
          {/* Stepper header */}
          <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${HAIRLINE}` }}>
            <WizardStepper
              steps={STEPS}
              active={tab}
              isStepEnabled={isStepEnabled}
              hasStepError={hasStepError}
              onStep={(i) => handleTabChange(null, i)}
            />
          </Box>

          <Box sx={{ p: 3 }}>
            {/* Alerts / errors */}
            {isCreatingWorkspace && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Please wait for the workspace to be scheduled. This could take
                up to a few minutes.
              </Alert>
            )}
            {datasetsError && !toMessageList(createError).length && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Could not load datasets. You can still create a workspace
                without attaching a dataset.
              </Alert>
            )}

            {/* Form content */}
            <Box
              component="form"
              noValidate
              autoComplete="off"
              // Fill the available width so the form grows/shrinks with the
              // content area (e.g. when the sidebar is collapsed) rather than
              // staying a fixed width and leaving dead space on the right.
              sx={{ width: "100%" }}
              onSubmit={(e) => {
                e.preventDefault();
                if (tab < STEPS.length - 1) handleNext();
              }}
            >
              {/* Step 1 — Workflow image */}
              <TabPanel value={tab} index={0}>
                <StepHeader
                  title="Workflow Image"
                  description="Optionally select a workflow image from the EFabric registry to pre-configure your workspace. Skip this step to use the default image."
                />
                <ImageSelector
                  imageRef={imageRef}
                  onImageRefChange={(value) => patchDraft({ imageRef: value })}
                  onImageRefErrorChange={setImageRefError}
                  disabled={isCreatingWorkspace}
                />
              </TabPanel>

              {/* Step 2 — Name & resource group */}
              <TabPanel value={tab} index={1}>
                <StepHeader
                  title="Name & Resource Group"
                  description="Give your workspace a unique name and choose which resource group to allocate it from."
                />
                <SubHeader>Workspace name</SubHeader>
                <Typography
                  sx={{ fontSize: FONT.sm, color: INK_SECONDARY, mb: 2 }}
                >
                  Provide a unique DNS-1123 compliant name. It identifies your
                  workspace within the EFabric cluster.
                </Typography>
                {!isLabelDNSValid && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    A valid workspace name is required.
                  </Alert>
                )}
                <TextField
                  required
                  fullWidth
                  id="workspace-name"
                  placeholder="my-workspace"
                  value={workspaceName}
                  onChange={handleNameChange}
                  helperText="A unique name for your workspace (DNS-1123 compliant)."
                  sx={{
                    mb: labelErrors.length || labelErrors.format ? 1 : 3,
                  }}
                  disabled={isCreatingWorkspace}
                  error={!!(labelErrors.length || labelErrors.format)}
                />
                {labelErrors.length && (
                  <FormHelperText error sx={{ ml: 2, mt: 0, mb: 2 }}>
                    Must contain no more than 63 characters.
                  </FormHelperText>
                )}
                {labelErrors.format && (
                  <FormHelperText error sx={{ ml: 2, mt: 0, mb: 2 }}>
                    Must contain only lowercase alphanumeric characters or{" "}
                    {`'-'`}. Must start & end with an alphanumeric character.
                  </FormHelperText>
                )}
                <SubHeader sx={{ mt: 4 }}>Resource group</SubHeader>
                <Typography
                  sx={{ fontSize: FONT.sm, color: INK_SECONDARY, mb: 2 }}
                >
                  Select the resource group to allocate this workspace from.
                </Typography>

                <ResourceGroupSelector
                  groups={profile?.groups || []}
                  selectedGroup={selectedGroup}
                  onGroupChange={(group) => applySelectedGroup(group)}
                  disabled={isCreatingWorkspace}
                />
              </TabPanel>

              {/* Step 3 — Resources */}
              <TabPanel value={tab} index={2}>
                <StepHeader
                  title={`Resource Configuration (${(workspaceType ?? "GPU").toUpperCase()})`}
                  description="Set the compute resources for your workspace. Runtime is fixed by the selected partition."
                />

                {workspaceType === "gpu" ? (
                  <GPUResourceForm
                    value={gpuConfig}
                    errors={gpuValidation.errors}
                    onChange={patchGpuConfig}
                    disabled={isCreatingWorkspace}
                    isSubmitJob={false}
                    accessibleGPUs={resourceAvailability.accessibleGPUs}
                    effectiveLimits={resourceAvailability.gpuEffectiveLimits}
                    runtimeField={runtimeField}
                  />
                ) : (
                  <CPUResourceForm
                    effectiveLimits={resourceAvailability.cpuEffectiveLimits}
                    value={cpuConfig}
                    errors={cpuValidation.errors}
                    onChange={patchCpuConfig}
                    disabled={isCreatingWorkspace}
                    runtimeField={runtimeField}
                  />
                )}

                <Alert severity="info" sx={{ mt: 3 }}>
                  Tip: maximum values are theoretical limits inferred from
                  cluster hardware. Your request may still fail if resources are
                  unavailable at submission time.
                </Alert>
              </TabPanel>

              {/* Step 4 — Datasets */}
              <TabPanel value={tab} index={3}>
                <StepHeader
                  title="Attach Datasets"
                  description="Optionally mount datasets into your workspace. You can skip this step and attach them later."
                />
                <DatasetSelector
                  availableDatasets={availableDatasets}
                  selectedDatasets={selectedDatasets}
                  onSelectedDatasetsChange={(value) =>
                    patchDraft({ selectedDatasets: value })
                  }
                  disabled={isCreatingWorkspace}
                  onGoToDatasets={onGoToDatasets}
                />
              </TabPanel>
            </Box>

            {/* Action bar */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                mt: 4,
                pt: 2.5,
                borderTop: `1px solid ${HAIRLINE}`,
              }}
            >
              {tab === 0 ? (
                <Button
                  variant="outlinedButton"
                  onClick={onCancel}
                  disabled={isCreatingWorkspace}
                >
                  Cancel
                </Button>
              ) : (
                <Button
                  variant="outlinedButton"
                  onClick={handleBack}
                  startIcon={<ChevronLeftIcon />}
                  disabled={isCreatingWorkspace}
                >
                  Back
                </Button>
              )}

              {tab < 3 ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {tab === 0 && !imageRef.trim() && (
                    <Typography sx={{ fontSize: FONT.xs, color: INK_MUTED }}>
                      No image selected — skip to use the default.
                    </Typography>
                  )}
                  <Button
                    variant="solidButton"
                    onClick={handleNext}
                    endIcon={<ChevronRightIcon />}
                    disabled={isCreatingWorkspace}
                  >
                    {tab === 0 && !imageRef.trim() ? "Skip" : "Next"}
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        bgcolor: validationMeta.color,
                      }}
                    />
                    <Typography sx={{ fontSize: FONT.md, color: INK_MUTED }}>
                      {validationMeta.label}
                    </Typography>
                  </Box>
                  {!configValidated ? (
                    <Button
                      variant="solidButton"
                      color="primary"
                      onClick={handleValidateConfig}
                      disabled={
                        !canValidateConfig ||
                        configValidation.status === "validating"
                      }
                    >
                      {configValidation.status === "validating" ? (
                        <CircularProgress size={24} sx={{ color: "white" }} />
                      ) : (
                        "Validate"
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="solidButton"
                      color="primary"
                      onClick={handleSubmit}
                      disabled={!canCreate}
                    >
                      {isCreatingWorkspace ? (
                        <CircularProgress size={24} sx={{ color: "white" }} />
                      ) : (
                        "Create"
                      )}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <ConfigPreviewPanel
          yaml={displayedYaml}
          validation={buildPreviewValidationProps({
            primaryError: createError,
            setPrimaryError: setCreateError,
            importError,
            setImportError,
            importSuccess,
            setImportSuccess,
            importWarnings,
            setImportWarnings,
          })}
          footer={
            <ConfigUploadFooter
              onUploadClick={() => importInputRef.current?.click()}
            />
          }
        />
        <input
          ref={importInputRef}
          type="file"
          accept=".yaml,.yml,text/yaml"
          hidden
          onChange={handleImportConfig}
        />
      </Box>

      <Dialog
        open={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
          onSuccess?.();
        }}
      >
        <DialogTitle>Workspace Successfully Scheduled</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your workspace has been scheduled. You can now check its status in
            the Workspaces section.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowSuccessDialog(false);
              onSuccess?.();
            }}
            variant="solidButton"
          >
            Confirm!
          </Button>
        </DialogActions>
      </Dialog>
    </MainPanelContent>
  );
};

CreateWorkspace.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
  onGoToDatasets: PropTypes.func,
};

export default CreateWorkspace;
